// Read side of ad attribution — mirrors server/services/adAttribution.js.
// Leads carry platform-specific keys in customFields; this collapses them
// into one { platform, campaign, adset, ad } record the badge, the leads
// list and the report all agree on.

export const PLATFORM_LABELS = {
  meta: 'Meta Ads',
  google: 'Google Ads',
  tiktok: 'TikTok Ads',
};

export const CHANNEL_LABELS = {
  lead_form: 'Lead form',
  messenger: 'Click-to-Messenger',
  instagram: 'Click-to-Instagram',
  whatsapp: 'Click-to-WhatsApp',
};

const clean = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s ? s : null;
};

export function cfGet(lead, key) {
  const cf = lead?.customFields;
  if (!cf) return undefined;
  if (typeof cf.get === 'function') return cf.get(key);
  return cf[key];
}

// Returns null when the lead has no paid-channel IDs at all.
export function getAdAttribution(lead) {
  if (!lead) return null;
  const g = (k) => clean(cfGet(lead, k));
  const organic = cfGet(lead, 'meta_is_organic') === true || cfGet(lead, 'meta_is_organic') === 'true';

  if (g('meta_ad_id') || g('meta_campaign_id') || g('meta_adset_id') || organic) {
    const surface = g('meta_platform');
    return {
      platform: 'meta',
      platformLabel: surface === 'ig' ? 'Instagram Ads' : PLATFORM_LABELS.meta,
      channel: g('meta_ad_channel') || 'lead_form',
      surface,
      isOrganic: organic,
      campaign: { id: g('meta_campaign_id'), name: g('meta_campaign_name') },
      adset: { id: g('meta_adset_id'), name: g('meta_adset_name') },
      ad: { id: g('meta_ad_id'), name: g('meta_ad_name') },
      form: { id: g('meta_form_id'), name: g('meta_form_name') },
      sourceUrl: g('meta_ad_source_url'),
    };
  }
  if (g('google_campaign_id') || g('google_adgroup_id') || g('google_creative_id')) {
    return {
      platform: 'google',
      platformLabel: PLATFORM_LABELS.google,
      channel: 'lead_form',
      surface: null,
      isOrganic: false,
      campaign: { id: g('google_campaign_id'), name: null },
      adset: { id: g('google_adgroup_id'), name: null },
      ad: { id: g('google_creative_id'), name: null },
      form: { id: g('google_form_id'), name: null },
      sourceUrl: null,
    };
  }
  if (g('tiktok_ad_id') || g('tiktok_campaign_id') || g('tiktok_adgroup_id')) {
    return {
      platform: 'tiktok',
      platformLabel: PLATFORM_LABELS.tiktok,
      channel: 'lead_form',
      surface: null,
      isOrganic: false,
      campaign: { id: g('tiktok_campaign_id'), name: null },
      adset: { id: g('tiktok_adgroup_id'), name: null },
      ad: { id: g('tiktok_ad_id'), name: null },
      form: { id: null, name: null },
      sourceUrl: null,
    };
  }
  return null;
}

// "Spring Promo › Homeowners 35+ › Video A" — falls back to short IDs so
// an un-synced lead still reads as *something* rather than nothing.
export function attributionLabel(attr, { withIds = true } = {}) {
  if (!attr) return '';
  const part = (node, kind) => {
    if (node?.name) return node.name;
    if (withIds && node?.id) return `${kind} #${shortId(node.id)}`;
    return null;
  };
  const parts = [part(attr.campaign, 'Campaign'), part(attr.adset, 'Ad set'), part(attr.ad, 'Ad')].filter(Boolean);
  if (!parts.length && attr.isOrganic) return 'Organic (form shared without an ad)';
  return parts.join(' › ');
}

export function shortId(id) {
  const s = String(id || '');
  return s.length > 8 ? `…${s.slice(-6)}` : s;
}

// Query string for the Leads list drill-down.
export function attributionLeadsQuery({ platform, campaignId, adsetId, adId } = {}) {
  const p = new URLSearchParams();
  if (adId) p.set('adId', adId);
  else if (adsetId) p.set('adsetId', adsetId);
  else if (campaignId) p.set('campaignId', campaignId);
  if (platform && !adId && !adsetId && !campaignId) p.set('adPlatform', platform);
  return p.toString();
}
